import type { VertragIntervall, VertragPosition, VertragRechnungKurz } from '../types';

export const INTERVALLE = ['MONATLICH', 'QUARTALSWEISE', 'JAEHRLICH'] as const;
export type { VertragIntervall };

const INTERVALL_TEXT: Record<VertragIntervall, string> = {
  MONATLICH: 'monatlich',
  QUARTALSWEISE: 'quartalsweise',
  JAEHRLICH: 'jährlich',
};

export function intervallText(i: VertragIntervall): string {
  return INTERVALL_TEXT[i] ?? String(i).toLowerCase();
}

export function versionLabel(nr: number): string {
  return `1.${nr}`;
}

/** Konditionen-Satz; identischer Wortlaut wie backend `vertragskonditionen_text`. */
export function konditionenText(a: {
  ist_vertrag?: boolean;
  vertrag_intervall?: string | null;
  vertrag_laufzeit_monate?: number | null;
  vertrag_kuendigungsfrist_tage?: number | null;
  vertrag_auto_verlaengerung?: boolean;
}): string | null {
  if (!a.ist_vertrag) return null;
  const intervall = a.vertrag_intervall
    ? intervallText(a.vertrag_intervall as VertragIntervall)
    : '';
  const laufzeit = a.vertrag_laufzeit_monate
    ? `Laufzeit ${a.vertrag_laufzeit_monate} Monate`
    : 'unbefristet';
  const frist = a.vertrag_kuendigungsfrist_tage ?? 30;
  const verl = a.vertrag_auto_verlaengerung ? 'mit automatischer' : 'ohne automatische';
  return `Vertrag mit regelmäßiger Abrechnung: ${intervall}, ${laufzeit}, Kündigungsfrist ${frist} Tage, ${verl} Verlängerung.`;
}

export type PositionsFeld = 'menge' | 'preis_einzeln' | 'rabatt_prozent' | 'beschreibung';

export interface PositionsAenderung {
  artikel_id: number;
  feld: PositionsFeld;
  von: string | number | null;
  nach: string | number | null;
}

/** API liefert Decimals als Strings; "" / null / NaN werden zu 0. */
export function betrag(v: string | number | null | undefined): number {
  const n = Number(v);
  return v == null || Number.isNaN(n) ? 0 : n;
}

type Zahl = string | number | null;
type PositionsEingabe = Omit<VertragPosition, 'menge' | 'preis_einzeln' | 'rabatt_prozent'> & {
  menge: string | number;
  preis_einzeln: string | number;
  rabatt_prozent: Zahl;
};

const NUMERISCH: PositionsFeld[] = ['menge', 'preis_einzeln', 'rabatt_prozent'];
const FELDER: PositionsFeld[] = ['menge', 'preis_einzeln', 'rabatt_prozent', 'beschreibung'];

/** Vergleicht zwei Versionsstände (Schlüssel artikel_id; bei Doppelungen zuerst positions_nr). */
export function positionsDiff<T extends PositionsEingabe = VertragPosition>(
  alt: T[],
  neu: T[],
): { neu: T[]; entfernt: T[]; geaendert: PositionsAenderung[] } {
  const rest = [...alt];
  const ergebnis = { neu: [] as T[], entfernt: [] as T[], geaendert: [] as PositionsAenderung[] };
  for (const n of neu) {
    let idx = rest.findIndex(a => a.positions_nr === n.positions_nr && a.artikel_id === n.artikel_id);
    if (idx < 0) idx = rest.findIndex(a => a.artikel_id === n.artikel_id);
    if (idx < 0) {
      ergebnis.neu.push(n);
      continue;
    }
    const [a] = rest.splice(idx, 1);
    for (const feld of FELDER) {
      const von = (a[feld] ?? null) as string | number | null;
      const nach = (n[feld] ?? null) as string | number | null;
      const gleich = NUMERISCH.includes(feld) ? betrag(von) === betrag(nach) : von === nach;
      if (!gleich) ergebnis.geaendert.push({ artikel_id: n.artikel_id as number, feld, von, nach });
    }
  }
  ergebnis.entfernt = rest;
  return ergebnis;
}

/** Summe je Periode: menge × preis × (1 − rabatt/100), je Position in ganzen Cent gerundet. */
export function periodenSumme(
  positionen: { menge: string | number; preis_einzeln: string | number; rabatt_prozent: Zahl }[],
): number {
  const cents = positionen.reduce((sum, p) => {
    const preisCent = Math.round(betrag(p.preis_einzeln) * 100);
    const faktor = 100 - betrag(p.rabatt_prozent);
    return sum + Math.round((betrag(p.menge) * preisCent * faktor) / 100);
  }, 0);
  return cents / 100;
}

/** Betrag als "1.234,50 €" (API-Decimals kommen als String); null/undefined → "—". */
export function euro(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  return betrag(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/** Jüngste Rechnung (Leistungszeitraum, dann ID), wenn sie storniert ist — sonst null.
 *  Grundlage für „Periode erneut abrechnen“. */
export function stornierteLetzteRechnung<T extends VertragRechnungKurz>(rechnungen: T[]): T | null {
  let letzte: T | null = null;
  for (const r of rechnungen) {
    if (!letzte) { letzte = r; continue; }
    const cmp = (r.leistungszeitraum_von ?? '').localeCompare(letzte.leistungszeitraum_von ?? '');
    if (cmp > 0 || (cmp === 0 && r.ausgangsrechnung_id > letzte.ausgangsrechnung_id)) letzte = r;
  }
  return letzte && letzte.status === 'STORNIERT' ? letzte : null;
}

/** Rollen, die vor dem frühesten Datum kündigen dürfen (backend `KULANZ_ROLLEN`). */
const KULANZ_ROLLEN = ['Admin', 'Geschäftsführer', 'SuperAdmin'];

export function darfKulanz(rollen: string[]): boolean {
  return rollen.some(r => KULANZ_ROLLEN.includes(r));
}

/** Frühestes zulässiges „Kündigung zum“ (wie backend `fruehestes_kuendigungsdatum`):
 *  max(heute + Kündigungsfrist, Laufzeitende bzw. heute), als ISO-Datum. */
export function fruehestesKuendigungsdatum(
  v: { kuendigungsfrist_tage: number; laufzeit_ende: string | null },
  heute: Date = new Date(),
): string {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const frist = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() + (v.kuendigungsfrist_tage || 0));
  const fristIso = iso(frist);
  const endeIso = v.laufzeit_ende ?? iso(heute);
  return fristIso > endeIso ? fristIso : endeIso;
}
